/**
 * Storage Service - OVH Object Storage
 * 
 * Service pour gérer les opérations de stockage de fichiers via l'API backend OVH.
 * Remplace les appels directs à Supabase Storage.
 * 
 * Features:
 * - Upload de fichiers (single & multiple)
 * - Génération d'URLs signées avec cache
 * - Téléchargement de fichiers
 * - Suppression de fichiers (batch)
 * 
 * @module services/storageService
 */

import { apiClient } from '@/lib/apiClient';
import { supabase } from '@/integrations/supabase/client';
import { API_BASE_URL } from '@/lib/apiBaseUrl';
import { triggerBlobDownload } from '@/utils/blobDownload';

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface UploadOptions {
  caseId?: string;
  category?: string;
  folderPath?: string[];
}

export interface UploadResult {
  fileName: string;
  path: string;
  size: number;
  contentType: string;
  uploadedAt: string;
  url?: string;
}

export interface SignedUrlOptions {
  expiresIn?: number;
  download?: boolean;
}

export interface RemoveResult {
  removed: string[];
  errors: Array<{ path: string; error: string }>;
}

/**
 * Manifest retourné par POST /storage/documents/pdf-thumbnails.
 * Chaque page expose 2 paths OVH : `thumbnailPath` (~75 DPI, ~30-50 KB) pour
 * les grilles de scindage, `zoomPath` (~200 DPI, ~200-400 KB) pour le lightbox.
 *
 * Le frontend fetch chaque path via `/storage/documents/download/<path>?inline=true`
 * (endpoint same-origin auth JWT) et convertit le blob en object URL. Ce détour
 * évite des bugs Safari sur les <img> cross-origin batch (CORS dual-cache, iCloud
 * Private Relay qui change l'IP source, V4 signature instable).
 */
export interface PdfThumbnailsManifest {
  pageCount: number;
  pages: Array<{
    pageNumber: number;
    thumbnailPath: string;
    zoomPath: string;
  }>;
}

interface SignedUrlResponse {
  signedUrl: string;
  expiresAt: string;
}

// Backend response structure (uses different property names)
interface BackendUploadResponse {
  fileName: string;
  filePath: string;  // Backend uses "filePath" not "path"
  fileSize: number;  // Backend uses "fileSize" not "size"
  contentType: string;
  uploadedAt: string;
  url?: string;
}

// ============================================================================
// Storage Service Class
// ============================================================================

/**
 * Délai accordé à un POST d'upload, quel que soit le poids du fichier : 5 minutes.
 *
 * `AbortSignal.timeout` borne la durée TOTALE de la requête, envoi du corps compris
 * (fetch n'expose pas la progression, donc pas de délai d'inactivité possible). À
 * 120 s, tout fichier de quelques Mo était coupé sur un lien montant lent : la
 * requête n'arrivait jamais au backend et les 3 tentatives échouaient à l'identique
 * (incident 2026-10-07 : 25 `AbortError` sur une session, 0 log serveur).
 * Mesuré ce jour-là chez le client concerné : ~85 Ko/s en envoi seul (2 Mo en 21 s),
 * soit ~14 Ko/s par fichier quand 6 envois se partagent le lien. Un PDF de 15 Mo
 * demande alors ~3 min seul, et un fichier de 2 Mo ~2 min 20 en lot : un plancher à
 * 120 s les coupait encore, d'où un délai unique plutôt que proportionnel au poids.
 * Borné à 5 min pour qu'un envoi réellement pendu finisse par échouer et être rejoué.
 * Limite connue : à ce débit, un fichier de 50 Mo (~10 min) ne passe toujours pas.
 */
export const UPLOAD_TIMEOUT_MS = 300_000;

class StorageService {
  // Cache des URLs signées avec leur expiration
  private urlCache: Map<string, { url: string; expiresAt: number }> = new Map();
  
  /**
   * Upload un ou plusieurs fichiers vers OVH Object Storage
   *
   * Un `AbortSignal.timeout` est créé À NEUF à chaque appel (jamais réutilisé) :
   * un signal déjà déclenché est « sticky » — le partager entre deux tentatives
   * de retry tuerait silencieusement la seconde. Cette méthode étant rappelée à
   * neuf par la boucle de retry de `uploadFileToFolder`, chaque tentative obtient
   * son propre timer. Sans ce timeout, un POST multipart bloqué (réseau qui drop
   * sans RST) pendait indéfiniment au lieu d'échouer vite et d'être rejouable —
   * cause de l'échec silencieux observé en prod (req. jamais arrivée au backend).
   */
  async uploadFile(
    file: File | File[],
    options?: UploadOptions
  ): Promise<UploadResult | UploadResult[]> {
    const formData = new FormData();

    // Support single ou multiple files
    const files = Array.isArray(file) ? file : [file];
    files.forEach(f => formData.append('file', f));

    if (options?.caseId) formData.append('caseId', options.caseId);
    if (options?.category) formData.append('category', options.category);
    if (options?.folderPath) formData.append('folderPath', JSON.stringify(options.folderPath));

    // Ne pas spécifier Content-Type pour FormData - le navigateur le gère automatiquement
    const response = await apiClient.post<BackendUploadResponse | BackendUploadResponse[]>(
      '/storage/documents/upload',
      formData,
      { signal: AbortSignal.timeout(UPLOAD_TIMEOUT_MS) }
    );
    
    // Transform backend response to frontend format
    const transformResponse = (backendRes: BackendUploadResponse): UploadResult => ({
      fileName: backendRes.fileName,
      path: backendRes.filePath,      // ✅ Map filePath → path
      size: backendRes.fileSize,      // ✅ Map fileSize → size
      contentType: backendRes.contentType,
      uploadedAt: backendRes.uploadedAt,
      url: backendRes.url
    });
    
    // Si single file, retourner data directement, sinon retourner array
    if (Array.isArray(response)) {
      return response.map(transformResponse);
    }
    return transformResponse(response);
  }

  /**
   * Génère une URL signée pour accès temporaire
   */
  async getSignedUrl(
    path: string,
    options?: SignedUrlOptions
  ): Promise<string> {
    const response = await apiClient.post<SignedUrlResponse>('/storage/documents/signed-url', {
      path,
      expiresIn: options?.expiresIn || 3600,
      download: options?.download || false
    });
    return response.signedUrl;
  }
  
  /**
   * Génère une URL signée avec cache automatique
   * Régénère si l'URL est expirée ou proche de l'expiration (< 5 min)
   */
  async getCachedSignedUrl(
    path: string,
    options?: SignedUrlOptions
  ): Promise<string> {
    const cacheKey = `${path}-${options?.download || false}`;
    const cached = this.urlCache.get(cacheKey);
    const now = Date.now();
    
    // Si en cache et pas expiré (avec marge de 5 min)
    if (cached && cached.expiresAt > now + 300000) {
      return cached.url;
    }
    
    // Générer nouvelle URL
    const response = await apiClient.post<SignedUrlResponse>('/storage/documents/signed-url', {
      path,
      expiresIn: options?.expiresIn || 3600,
      download: options?.download || false
    });
    
    // Mettre en cache
    this.urlCache.set(cacheKey, {
      url: response.signedUrl,
      expiresAt: new Date(response.expiresAt).getTime()
    });
    
    return response.signedUrl;
  }

  /**
   * Télécharge un fichier et déclenche le download dans le navigateur
   */
  async triggerDownload(path: string, fileName: string): Promise<void> {
    const encodedPath = encodeURIComponent(path);
    const url = `${API_BASE_URL}/storage/documents/download/${encodedPath}?fileName=${encodeURIComponent(fileName)}&inline=false`;
    
    const token = await this.getAuthToken();
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) throw new Error('Download failed');
    
    triggerBlobDownload(await response.blob(), fileName);
  }
  
  /**
   * Télécharge un fichier et retourne le Blob (pour usage programmatique)
   */
  async downloadFile(path: string): Promise<Blob> {
    const encodedPath = encodeURIComponent(path);
    const url = `${API_BASE_URL}/storage/documents/download/${encodedPath}`;
    
    const token = await this.getAuthToken();
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) throw new Error('Download failed');
    return await response.blob();
  }
  
  /**
   * Fetch a file as an inline blob URL via backend proxy (no CORS issues).
   */
  async getInlineBlobUrl(path: string, mimeType: string): Promise<string> {
    const encodedPath = encodeURIComponent(path);
    const url = `${API_BASE_URL}/storage/documents/download/${encodedPath}?inline=true`;

    const token = await this.getAuthToken();
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) throw new Error('Inline fetch failed');
    const blob = await response.blob();
    return URL.createObjectURL(new Blob([blob], { type: mimeType }));
  }

  /**
   * Fetch a file as a `data:` URL (base64 inline).
   *
   * Pourquoi pas un object URL (`blob:…`) ? Sur Safari/WebKit, quand l'object
   * URL est créé depuis une `Response` issue d'un fetch cross-origin (ici
   * :8080 → :5001 en dev, et frontend/backend séparés en prod), `<img>` ne
   * rend rien : le blob est marqué "tainted" et l'image reste blanche
   * silencieusement (pas d'erreur console). Le data URL ne dépend d'aucune
   * origine — il est inline dans le DOM — donc neutralise ce bug.
   *
   * Coût : +33% sur la taille en mémoire vs un blob (encodage base64) et un
   * peu plus de CPU au décodage. Pour des thumbnails JPEG ~30-50 KB c'est
   * négligeable (~1.5 MB pour 30 pages).
   */
  async getInlineDataUrl(path: string): Promise<string> {
    const encodedPath = encodeURIComponent(path);
    const url = `${API_BASE_URL}/storage/documents/download/${encodedPath}?inline=true`;

    const token = await this.getAuthToken();
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) throw new Error(`Inline fetch failed (${response.status})`);
    const blob = await response.blob();

    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Rasterise toutes les pages d'un PDF en JPEG côté backend (pdftoppm) et
   * renvoie un manifest avec signed URLs pour chaque page (small + large).
   *
   * Sert le flux scindage des pièces : au lieu de rendre les thumbnails dans
   * le navigateur via pdfjs (~2-3 min sur un PDF image-heavy de 38 pages),
   * on charge des `<img>` plats depuis OVH (~10-15s la 1ʳᵉ fois, instantané
   * ensuite via cache content-hash).
   */
  async getPdfThumbnails(path: string): Promise<PdfThumbnailsManifest> {
    const token = await this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/storage/documents/pdf-thumbnails`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ path }),
    });

    if (!response.ok) {
      const body = await response.text();
      throw new Error(`PDF thumbnails fetch failed (${response.status}): ${body.slice(0, 200)}`);
    }

    return response.json() as Promise<PdfThumbnailsManifest>;
  }

  /**
   * Convertit un document en PDF côté backend et renvoie une blob URL prête à être
   * consommée par le viewer PDF natif du navigateur.
   * - .docx/.doc → LibreOffice (fidélité de rendu Word, vs `docx-preview` qui échouait
   *   sur les structures non triviales).
   * - .eml/.msg  → EmailConverterService (mailparser/.msgreader → wkhtmltopdf) : rendu
   *   fidèle des emails HTML + listing des pièces jointes, et seul moyen de prévisualiser
   *   un .msg Outlook (aucun parser client).
   *
   * Le backend cache le résultat (clé content-hash) → la 1ʳᵉ vue prend 2-5s, les
   * suivantes sont quasi instantanées.
   */
  async getConvertedPdfBlobUrl(path: string): Promise<string> {
    return URL.createObjectURL(await this.getConvertedPdfBlob(path));
  }

  /** Même conversion, rendue en octets : la fusion de pièces l'assemble avec d'autres PDF. */
  async getConvertedPdfBlob(path: string): Promise<Blob> {
    const token = await this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/storage/documents/convert-to-pdf`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ path }),
    });

    if (!response.ok) {
      // Le backend renvoie un AppError JSON avec userMessage pour les erreurs métier.
      const errorBody = await response.text();
      throw new Error(`PDF conversion failed (${response.status}): ${errorBody.slice(0, 200)}`);
    }

    const blob = await response.blob();
    return new Blob([blob], { type: 'application/pdf' });
  }

  /**
   * Ouvre un fichier dans un nouvel onglet pour visualisation
   */
  async viewFile(path: string, _fileName: string): Promise<void> {
    const url = await this.getCachedSignedUrl(path, { download: false });
    window.open(url, '_blank');
  }

  /**
   * Supprime un ou plusieurs fichiers
   */
  async removeFiles(paths: string | string[]): Promise<RemoveResult> {
    const pathsArray = Array.isArray(paths) ? paths : [paths];
    
    // DELETE avec body nécessite fetch direct
    const token = await this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/storage/documents/remove`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ paths: pathsArray })
    });
    
    if (!response.ok) throw new Error('Remove failed');
    const result = await response.json() as RemoveResult;
    
    // Invalider le cache pour ces paths
    pathsArray.forEach(path => {
      this.urlCache.delete(`${path}-false`);
      this.urlCache.delete(`${path}-true`);
    });
    
    return result;
  }
  
  /**
   * Vide le cache d'URLs signées
   */
  clearCache(): void {
    this.urlCache.clear();
  }
  
  /**
   * Récupère le token d'authentification Supabase
   */
  private async getAuthToken(): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || '';
  }
}

// Export singleton instance
export const storageService = new StorageService();
